import { formatPolicyTypeName } from "@/src/features/internalUsers/utils/promptPolicyUtils"
import type { PromptPolicy } from "@/src/types/promptPolicy"

import Box from "@mui/material/Box"
import Dialog from "@mui/material/Dialog"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import Stack from "@mui/material/Stack"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { Iconify } from "@/src/common/iconify"

export type PromptPolicyCompareState = {
    policy: PromptPolicy
    workspaceName: string
    globalPrompt: string
    workspacePrompt: string
}

type PromptPolicyCompareDialogProps = {
    compare: PromptPolicyCompareState | null
    onClose: () => void
}

const readOnlyFieldSx = {
    "& .MuiInputBase-input": {
        fontFamily: "monospace",
        fontSize: "0.8125rem",
        lineHeight: 1.5,
    },
}

export function PromptPolicyCompareDialog({ compare, onClose }: PromptPolicyCompareDialogProps) {
    const open = !!compare

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle sx={{ display: "flex", alignItems: "flex-start", pr: 6 }}>
                <Box>
                    <Typography variant="h6">Compare prompts</Typography>
                    {compare && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {formatPolicyTypeName(compare.policy.policyType)} — {compare.workspaceName}
                        </Typography>
                    )}
                </Box>
                <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
                    <Iconify icon="mdi:close" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Global (default)
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={14}
                            maxRows={22}
                            value={compare?.globalPrompt ?? ""}
                            InputProps={{ readOnly: true }}
                            sx={readOnlyFieldSx}
                        />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Workspace override
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={14}
                            maxRows={22}
                            value={compare?.workspacePrompt ?? ""}
                            InputProps={{ readOnly: true }}
                            sx={readOnlyFieldSx}
                        />
                    </Box>
                </Stack>
            </DialogContent>
        </Dialog>
    )
}
